import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useConfig } from '../config';

export default function Terms() {
  const { t } = useTranslation();
  const cfg = useConfig();
  const interp = {
    platformFee: cfg.platform_fee_percent,
    hostShare: cfg.guide_fee_percent,
    cancelFee: cfg.cancellation_fee_percent,
    freeHours: cfg.traveler_free_cancel_hours,
    graceHours: cfg.traveler_grace_hours,
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
          {t('terms.title')}
        </h1>

        {/* Legal Disclaimer Box */}
        <Card className="bg-[#F9004D]/10 border-[#F9004D]/50 p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-[#F9004D] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-white mb-3">{t('terms.disclaimer.title')}</h2>
              <p className="text-zinc-300 leading-relaxed mb-3">
                {t('terms.disclaimer.p1')}
              </p>
              <p className="text-zinc-300 leading-relaxed mb-3">
                <strong className="text-[#F9004D]">Important :</strong> {t('terms.disclaimer.p2')}
              </p>
              <p className="text-zinc-300 leading-relaxed">
                {t('terms.disclaimer.p3')}
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-8">
          {/* Article 1 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art1.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art1.intro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t('terms.art1.li1')}</li>
                <li>{t('terms.art1.li2')}</li>
                <li>{t('terms.art1.li3')}</li>
                <li>{t('terms.art1.li4')}</li>
                <li><strong>{t('terms.art1.li5')}</strong></li>
              </ul>
              <p className="mt-4 bg-zinc-800/50 p-4 rounded-lg border border-white/10">
                <strong>{t('terms.art1.note')}</strong>
              </p>
            </div>
          </Card>

          {/* Article 2 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art2.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art2.intro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>{t('terms.art2.li1')}</strong></li>
                <li>{t('terms.art2.li2')}</li>
                <li>{t('terms.art2.li3')}</li>
              </ul>
            </div>
          </Card>

          {/* Article 3 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art3.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art3.intro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t('terms.art3.li1')}</li>
                <li>{t('terms.art3.li2')}</li>
                <li>{t('terms.art3.li3')}</li>
                <li>{t('terms.art3.li4')}</li>
                <li>{t('terms.art3.li5')}</li>
              </ul>
            </div>
          </Card>

          {/* Article 4 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art4.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art4.intro')}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t('terms.art4.li1')}</li>
                <li>{t('terms.art4.li2')}</li>
                <li>{t('terms.art4.li3')}</li>
                <li>{t('terms.art4.li4')}</li>
              </ul>
              <p className="mt-4 text-sm italic">
                {t('terms.art4.note')}
              </p>
            </div>
          </Card>

          {/* Article 5 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art5.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t('terms.art5.li1')}</li>
                <li>{t('terms.art5.li2', interp)}</li>
                <li>{t('terms.art5.li3', interp)}</li>
                <li>{t('terms.art5.li4', interp)}</li>
                <li>{t('terms.art5.li5')}</li>
              </ul>
            </div>
          </Card>

          {/* Article 6 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art6.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art6.p1')}</p>
            </div>
          </Card>

          {/* Article 7 */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.art7.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.art7.p1')}</p>
            </div>
          </Card>

          {/* Contact */}
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t('terms.contact.title')}
            </h2>
            <div className="space-y-3 text-zinc-300">
              <p>{t('terms.contact.p1')}</p>
              <p className="text-[#00F0FF]">contact@guidematch-tokyo.com</p>
            </div>
          </Card>
        </div>

        <div className="mt-12 text-center text-zinc-500 text-sm">
          <p>{t('terms.updated')}</p>
        </div>
      </div>
    </div>
  );
}
